import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as ActiveIcon,
  Build as MaintenanceIcon,
  Cancel as InactiveIcon,
  DirectionsCar as CarIcon,
  VpnKey as LicenseIcon,
  Accessible as WheelchairIcon,
  AirlineSeatReclineExtra as TragestuhlIcon,
  Hotel as LiegendIcon,
  TimeToLeave as KombiIcon,
  Star as LimousineIcon,
  ArrowDownward as TieferEinstiegIcon,
  ArrowUpward as HoherEinstiegIcon
} from '@mui/icons-material';
import { getLicenseInfo } from '../../config/vehicleLimitConfig';
import { styled } from '@mui/material/styles';

import {
  fetchVehicles,
  deleteVehicle,
  releaseVehicle
  // OLD LISTENERS DEAKTIVIERT - AppDataManager übernimmt Datenladung
  // startVehiclesStatusListener,
  // stopVehiclesStatusListener
} from '../../redux/slices/vehiclesSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { fetchOperators } from '../../redux/slices/operatorsSlice';
import { isOperatorRole } from '../../redux/slices/authSlice';
import { getVehicleTypeLabel, getVehicleTypeSeats } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let backgroundColor;
  let textColor = 'white';
  let icon;
  
  switch (status) {
    case 'active':
      backgroundColor = theme.palette.success.main;
      icon = <ActiveIcon />;
      break;
    case 'maintenance':
      backgroundColor = theme.palette.warning.main;
      icon = <MaintenanceIcon />;
      break;
    case 'inactive':
      backgroundColor = theme.palette.error.main;
      icon = <InactiveIcon />;
      break;
    case 'available':
      backgroundColor = theme.palette.success.main;
      icon = <ActiveIcon />;
      break;
    case 'in_use':
      backgroundColor = theme.palette.primary.main;
      icon = <CarIcon />;
      break;
    default:
      backgroundColor = undefined; // Verwende Standard-Hintergrund
      textColor = 'inherit';
      icon = <InactiveIcon />;
      break;
  }
  
  return {
    color: textColor,
    backgroundColor,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

// Statische Maps – einmalig erstellt, nie neu berechnet
const STATUS_LABEL_MAP = {
  'active': 'Verfügbar',
  'maintenance': 'Wartung',
  'inactive': 'Inaktiv',
  'available': 'Verfügbar',
  'in_use': 'Im Einsatz',
};

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const VehiclesList = () => {
  const dispatch = useDispatch();
  // Verwende neues AppData System statt altes vehicles slice (memoized)
  const vehicles = useSelector(state => state.appData.vehicles);
  const drivers = useSelector(state => state.appData.drivers); // Für Fahrer-Zuordnung
  const isLoading = useSelector(state => state.appData.isLoading.vehicles);
  const error = useSelector(state => state.appData.error.vehicles);

  // Aktueller Nutzer – bestimmt, welches Fahrzeuglimit gilt.
  const currentUser = useSelector(state => state.auth?.user);
  const isOperatorAccount = isOperatorRole(currentUser);
  const currentOperatorId = currentUser?.operatorId || null;

  // Lizenzlimit: Sub-Unternehmer (Operator) sehen ihr eigenes, vom zentralen
  // Admin zugewiesenes Limit; zentrale Konten das globale Firestore-Limit.
  const globalVehicleLimit = useSelector(state => state.settings?.global?.vehicleLimit);
  const operators = useSelector(state => state.operators?.operators);
  const vehicleLimitFromDB = useMemo(() => {
    if (isOperatorAccount) {
      const ownOperator = (operators || []).find(op => op.id === currentOperatorId);
      return ownOperator?.vehicleLimit;
    }
    return globalVehicleLimit;
  }, [isOperatorAccount, operators, currentOperatorId, globalVehicleLimit]);
  const settingsInitialized = useSelector(state => state.settings?.isInitialized);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('vehicle_number');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVehicle, setMenuVehicle] = useState(null);
  
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (!settingsInitialized) {
      dispatch(fetchSettings());
    }

    // Sub-Unternehmer benötigen ihren eigenen Operator-Datensatz (vehicleLimit).
    if (isOperatorAccount) {
      dispatch(fetchOperators());
    }

    const loadVehicles = async () => {
      try {
        await dispatch(fetchVehicles());
        setDataLoaded(true);
      } catch (error) {
        console.error("VehiclesList: Fehler beim Laden der Fahrzeugdaten:", error);
        setDataLoaded(false);
      }
    };

    if (!dataLoaded) {
      loadVehicles();
    }
  }, [dispatch, dataLoaded, settingsInitialized, isOperatorAccount]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  }, []);

  const handleSortByChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuVehicle(null);
  }, []);

  const handleOpenMenu = useCallback((event, vehicle) => {
    setMenuAnchor(event.currentTarget);
    setMenuVehicle(vehicle);
  }, []);

  const handleDeleteClick = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuVehicle(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedVehicle) {
      dispatch(deleteVehicle(selectedVehicle.id));
      setDeleteDialogOpen(false);
      setSelectedVehicle(null);
    }
  }, [dispatch, selectedVehicle]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedVehicle(null);
  }, []);

  const handleReleaseClick = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setReleaseDialogOpen(true);
    setMenuAnchor(null);
    setMenuVehicle(null);
  }, []);

  const handleReleaseConfirm = useCallback(() => {
    if (selectedVehicle) {
      dispatch(releaseVehicle(selectedVehicle.id));
      setReleaseDialogOpen(false);
      setSelectedVehicle(null);
    }
  }, [dispatch, selectedVehicle]);

  const handleReleaseCancel = useCallback(() => {
    setReleaseDialogOpen(false);
    setSelectedVehicle(null);
  }, []);

  const getVehicleStatus = useCallback((vehicle) => {
    return vehicle.inUse === true
      ? { label: 'Im Einsatz', color: 'primary' }
      : { label: 'Verfügbar', color: 'success' };
  }, []);

  const translateStatus = useCallback((status) => STATUS_LABEL_MAP[status] || status, []);

  const formatTimestamp = useCallback((ts) => {
    if (!ts) return '-';
    try {
      let date;
      if (ts.toDate) {
        date = ts.toDate();
      } else if (ts.seconds) {
        date = new Date(ts.seconds * 1000);
      } else {
        date = new Date(ts);
      }
      return DATE_FORMATTER.format(date);
    } catch {
      return 'Ungültiges Datum';
    }
  }, []);

  const getDriverName = useCallback((driverId) => {
    if (!driverId) return null;
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : null;
  }, [drivers]);

  // Lizenzinfo – nur neu berechnet wenn sich vehicleLimitFromDB oder vehicles.length ändert
  const licenseStatus = useMemo(() => {
    const licenseInfo = getLicenseInfo(vehicleLimitFromDB);
    const vehicleCount = vehicles.length;
    return {
      licenseInfo,
      vehicleCount,
      isNearLimit: vehicleCount >= licenseInfo.limit * 0.8,
      isAtLimit: vehicleCount >= licenseInfo.limit,
    };
  }, [vehicleLimitFromDB, vehicles.length]);

  // Filter und Sort – wird nur neu berechnet wenn vehicles oder Filterkriterien sich ändern.
  // Echtzeit-Updates von Firebase (via AppDataManager → Redux) triggern automatisch eine
  // Neuberechnung, da `vehicles` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return vehicles.filter(vehicle => {
      const searchMatch =
        vehicle.vehicleNumber?.toLowerCase().includes(term) ||
        vehicle.model?.toLowerCase().includes(term) ||
        vehicle.licensePlate?.toLowerCase().includes(term);
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'available' && !vehicle.inUse) ||
        (statusFilter === 'in_use' && vehicle.inUse === true);
      return searchMatch && statusMatch;
    }).sort((a, b) => {
      // Status-Vergleich als gemeinsamer Fallback: Verfügbar (= !inUse) vor Belegt.
      const statusDiff = (a.inUse ? 1 : 0) - (b.inUse ? 1 : 0);
      switch (sortBy) {
        case 'vehicle_number': {
          // Fahrzeuge ohne Nummer ans Ende; sonst numerisch-natürlich vergleichen.
          const aHas = !!a.vehicleNumber;
          const bHas = !!b.vehicleNumber;
          if (aHas !== bHas) return aHas ? -1 : 1;
          const numberDiff = (a.vehicleNumber || '').localeCompare(b.vehicleNumber || '', undefined, { numeric: true });
          return numberDiff !== 0 ? numberDiff : statusDiff;
        }
        case 'model':
          return a.model.localeCompare(b.model);
        case 'license_plate':
          return a.licensePlate.localeCompare(b.licensePlate);
        case 'year':
          return (b.year || 0) - (a.year || 0);
        case 'status':
          return statusDiff;
        default:
          return 0;
      }
    });
  }, [vehicles, searchTerm, statusFilter, sortBy]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Fahrzeuge werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrzeuge"
        message={error}
        onRetry={() => dispatch(fetchVehicles())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Fahrzeugverwaltung"
        description="Übersicht und Verwaltung aller Fahrzeuge"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' }
        ]}
        actionButton={licenseStatus.isAtLimit ? null : {
          label: 'Fahrzeug hinzufügen',
          icon: <AddIcon />,
          path: '/fahrzeuge/neu'
        }}
      />
      
      {/* Lizenzinformation Banner */}
      <Box sx={{ mb: 3 }}>
        <Alert
          severity={licenseStatus.isAtLimit ? "error" : licenseStatus.isNearLimit ? "warning" : "info"}
          icon={<LicenseIcon />}
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Typography variant="body1">
                {isOperatorAccount ?
                  (licenseStatus.isAtLimit ?
                    `Fahrzeuglimit erreicht: ${licenseStatus.vehicleCount}/${licenseStatus.licenseInfo.limit} Fahrzeuge` :
                    `Fahrzeugnutzung: ${licenseStatus.vehicleCount}/${licenseStatus.licenseInfo.limit} Fahrzeuge`) :
                  (licenseStatus.isAtLimit ?
                    `Fahrzeuglimit erreicht: ${licenseStatus.vehicleCount}/${licenseStatus.licenseInfo.limit} Fahrzeuge (${licenseStatus.licenseInfo.tierName} Lizenz)` :
                    `Fahrzeugnutzung: ${licenseStatus.vehicleCount}/${licenseStatus.licenseInfo.limit} Fahrzeuge (${licenseStatus.licenseInfo.tierName} Lizenz)`)
                }
              </Typography>
              {licenseStatus.isAtLimit && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {isOperatorAccount ?
                    'Bitte wenden Sie sich an die Zentrale, um weitere Fahrzeuge für Ihr Unternehmen freizuschalten.' :
                    'Ihre Lizenz erlaubt keine weiteren Fahrzeuge.'
                  }
                </Typography>
              )}
            </Box>
            {licenseStatus.isAtLimit && !isOperatorAccount && (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                component={Link}
                to="#"
                onClick={() => alert('Bitte kontaktieren Sie den Support für ein Lizenz-Upgrade.')}
              >
                Lizenz erweitern
              </Button>
            )}
          </Box>
        </Alert>
      </Box>
      
      {/* Filter und Suche */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Nummer, Modell oder Kennzeichen"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="available">Verfügbar</MenuItem>
                <MenuItem value="in_use">In Benutzung</MenuItem>
                <MenuItem value="maintenance">Wartung</MenuItem>
                <MenuItem value="inactive">Inaktiv</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="model">Modell</MenuItem>
                <MenuItem value="vehicle_number">Fahrzeugnummer</MenuItem>
                <MenuItem value="license_plate">Kennzeichen</MenuItem>
                <MenuItem value="year">Baujahr</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Fahrzeuge gefunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Mobile Fahrzeugkarten */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {paginatedData.length > 0 ? (
          paginatedData.map((vehicle) => {
            const vehicleStatus = getVehicleStatus(vehicle);
            const driverName = vehicle.driverId ? getDriverName(vehicle.driverId) : null;
            return (
              <Card key={vehicle.id} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
                      <CarIcon sx={{ fontSize: 34, color: 'primary.main', mt: 0.25 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                            {vehicle.model || 'Unbekanntes Fahrzeug'}
                          </Typography>
                          <Chip
                            label={vehicle.vehicleNumber || '-'}
                            size="small"
                            variant={vehicle.vehicleNumber ? 'filled' : 'outlined'}
                            color={vehicle.vehicleNumber ? 'primary' : 'default'}
                            sx={{ height: 22 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {vehicle.licensePlate || 'Kein Kennzeichen'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`vehicle-menu-${vehicle.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, vehicle)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <StatusChip
                          label={vehicleStatus.label}
                          status={vehicle.inUse ? 'in_use' : 'available'}
                          size="small"
                          icon={vehicle.inUse ? <CarIcon /> : <ActiveIcon />}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Typ</Typography>
                      <Typography variant="body2">{getVehicleTypeLabel(vehicle.vehicleType)}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Kapazität</Typography>
                      <Typography variant="body2">
                        {getVehicleTypeSeats(vehicle.vehicleType) ? `${getVehicleTypeSeats(vehicle.vehicleType)} Sitzplätze` : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Zuletzt genutzt</Typography>
                      <Typography variant="body2">{formatTimestamp(vehicle.lastActivityTimestamp)}</Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Aktueller Fahrer</Typography>
                      <Typography variant="body2">{driverName || (vehicle.driverId ? 'Fahrer nicht gefunden' : 'Kein Fahrer zugewiesen')}</Typography>
                    </Grid>
                  </Grid>

                  {vehicle.specialCapabilities?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.25 }}>
                      {vehicle.specialCapabilities.map((capability) => (
                        <Chip
                          key={capability}
                          size="small"
                          variant="outlined"
                          label={capability}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/fahrzeuge/${vehicle.id}`}
                    sx={{ mt: 1.5 }}
                  >
                    Details anzeigen
                  </Button>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">Keine Fahrzeuge gefunden</Typography>
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '.MuiTablePagination-toolbar': {
              px: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      {/* Desktop Fahrzeugtabelle */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table sx={{ minWidth: 650 }} aria-label="Fahrzeugtabelle">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nr.</TableCell>
              <TableCell>Fahrzeug</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Kennzeichen</TableCell>
              <TableCell align="center">Kapazität</TableCell>
              <TableCell align="center">Zuletzt Genutzt</TableCell>
              <TableCell align="center">Aktueller Fahrer</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((vehicle) => (
                <StyledTableRow key={vehicle.id}>
                  <TableCell align="center">
                    <Chip
                      label={vehicle.vehicleNumber || '-'}
                      size="small"
                      variant={vehicle.vehicleNumber ? 'filled' : 'outlined'}
                      color={vehicle.vehicleNumber ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CarIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle2">{vehicle.model}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {getVehicleTypeLabel(vehicle.vehicleType)}
                          </Typography>
                          {vehicle.specialCapabilities?.includes('rollstuhl') && (
                            <Tooltip title="Rollstuhlfahrzeug"><WheelchairIcon sx={{ fontSize: 16, color: 'info.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('tragestuhl') && (
                            <Tooltip title="Tragestuhlfahrzeug"><TragestuhlIcon sx={{ fontSize: 16, color: 'warning.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('liegend') && (
                            <Tooltip title="Liegendfahrzeug"><LiegendIcon sx={{ fontSize: 16, color: 'error.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('kombi') && (
                            <Tooltip title="Kombi"><KombiIcon sx={{ fontSize: 16, color: 'success.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('limousine') && (
                            <Tooltip title="Limousine"><LimousineIcon sx={{ fontSize: 16, color: 'secondary.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('tiefer_einstieg') && (
                            <Tooltip title="Tiefer Einstieg"><TieferEinstiegIcon sx={{ fontSize: 16, color: 'primary.main' }} /></Tooltip>
                          )}
                          {vehicle.specialCapabilities?.includes('hoher_einstieg') && (
                            <Tooltip title="Hoher Einstieg"><HoherEinstiegIcon sx={{ fontSize: 16, color: 'primary.main' }} /></Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip 
                      label={getVehicleStatus(vehicle).label}
                      status={vehicle.inUse ? 'in_use' : 'available'}
                      size="small"
                      icon={vehicle.inUse ? <CarIcon /> : <ActiveIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">{vehicle.licensePlate}</TableCell>
                  <TableCell align="center">
                    {getVehicleTypeSeats(vehicle.vehicleType) ? `${getVehicleTypeSeats(vehicle.vehicleType)} Sitzplätze` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {formatTimestamp(vehicle.lastActivityTimestamp)}
                  </TableCell>
                  <TableCell align="center">
                    {vehicle.driverId ? (
                      <Typography variant="body2" color={getDriverName(vehicle.driverId) ? 'textPrimary' : 'textSecondary'}>
                        {getDriverName(vehicle.driverId) || 'Fahrer nicht gefunden'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Kein Fahrer zugewiesen
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`vehicle-menu-${vehicle.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, vehicle)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrzeuge gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuVehicle ? `vehicle-menu-${menuVehicle.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuVehicle ? `/fahrzeuge/${menuVehicle.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrzeug anzeigen
        </MenuItem>
        <MenuItem
          component={Link}
          to={menuVehicle ? `/fahrzeuge/${menuVehicle.id}/bearbeiten` : '#'}
          state={{ from: '/fahrzeuge' }}
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        {menuVehicle?.inUse && menuVehicle?.current_driver && (
          <MenuItem
            onClick={() => menuVehicle && handleReleaseClick(menuVehicle)}
            sx={{ color: 'success.main' }}
          >
            <ActiveIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrzeug freigeben
          </MenuItem>
        )}
        <MenuItem
          onClick={() => menuVehicle && handleDeleteClick(menuVehicle)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrzeug löschen"
        content={`Möchten Sie das Fahrzeug ${selectedVehicle?.model || ''} (${selectedVehicle?.licensePlate || ''}) wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Freigeben-Dialog */}
      <DeleteConfirmDialog
        open={releaseDialogOpen}
        title="Fahrzeug freigeben"
        content={`Möchten Sie das Fahrzeug ${selectedVehicle?.model || ''} (${selectedVehicle?.licensePlate || ''}) wirklich freigeben?`}
        confirmText="Fahrzeug freigeben"
        onConfirm={handleReleaseConfirm}
        onCancel={handleReleaseCancel}
      />
    </Box>
  );
};

export default VehiclesList;
